import Axios from "axios";
import sessionExpired from "../Core/sessionExpired";
import { logAuthError } from "./logAuthError";
import { getTokenObj, isCySsoToken } from "./tokenManager";
import { doMfeTokenRefresh, adoptTokensFromSiblingContext } from "./mfeRefreshCoordinator";
import {
  refreshTokens,
  isGenuineRefreshFailure,
  isRefreshRaceLost,
} from "./refreshTokens";
import { saveSession } from "../utils/saveSession";
import { isLoggedOut } from "../utils/cookieUtils";

// Fire sessionExpired at most once per page life — repeated 401s (a dashboard
// fires many at once) must not stack multiple redirects.
let sessionEnded = false;

// Shared across ALL createApiClient instances so only one refresh call fires
// even when multiple API instances (API, API_EPICENTER, …) simultaneously
// receive a 401. Also prevents multiple sessionExpired swal dialogs.
let isRefreshing = false;
let refreshQueue = [];

// ── Session-expiry (strict: ZERO tolerance after a refresh) ─────────────────
// Mirror of Sandbox's createApiClient. Logout happens when the session can't
// continue, two ways:
//   (a) the REFRESH fails for a GENUINE reason — the server responds (401 invalid
//       tokens, 500, or 200 with valid:false). A pure connectivity error (offline /
//       no `response`) does NOT log out — the session is kept and a later request
//       refreshes once the user is back online. (See refreshTokens.js →
//       isGenuineRefreshFailure.) This is what fixes the 500-refresh "stuck" bug
//       without logging out users who merely lost internet.
//   (b) ANY request still 401s AFTER a successful refresh → with a fresh token
//       every API should work, so a 401 now means the session is dead → logout.
// No counting, no tolerance. A normal request's 403 (or other non-401) is a
// permission/forbidden error and never logs out — only a 401 enters the refresh
// path above.
//
// EXCEPTION — lost refresh race (`recentDeletion` → isRefreshRaceLost): the
// session is ALIVE, another tab/app just rotated the tokens first. Never a
// logout; recovered by adoptTokensFromSiblingContext (mfeRefreshCoordinator).
// Without this, every concurrent refresh across two tabs (or portal +
// standalone sandbox) logged the losing context out — and its sessionExpired()
// cascade (cy_logged_out + cookie wipe + storage clear) then tore down the
// WINNING context too.

const storeExpiredApis = (cb) => {
  refreshQueue.push(cb);
};

const onRefreshed = (token) => {
  refreshQueue.forEach((cb) => cb(token, null));
  refreshQueue = [];
};

const onRefreshFailed = (err) => {
  refreshQueue.forEach((cb) => cb(null, err));
  refreshQueue = [];
};

const createApiClient = function (baseUrl) {
  this.apiBase = baseUrl;
  this.axios = Axios.create({
    baseURL: this.apiBase,
    timeout: 100000000,
  });

  this.axios.defaults.headers.post["Content-Type"] = "application/json";

  this.axios.interceptors.request.use(
    (req) => {
      const tokenObj = getTokenObj();
      const accessToken = tokenObj?.ACCESS_TOKEN;
      // Legacy (pre-cy-sso) tokens lack `accessTokenSecretId`. Token migration
      // has been removed — log these users out so they re-login and get a fresh
      // cy-sso token. (Sending the old token 401s and refreshing it 500s, which
      // left old users stuck — never logged out.) Only acts when a token exists,
      // so login / public pages are unaffected.
      if (accessToken && !isCySsoToken(accessToken)) {
        if (!sessionEnded) {
          sessionEnded = true;
          sessionExpired();
        }
        return Promise.reject(new Error("LEGACY_SESSION_LOGOUT"));
      }
      req.headers["Authorization"] = `Bearer ${accessToken || ""}`;
      req.headers["refresh-token"] = tokenObj?.REFRESH_TOKEN || "";
      req.headers["auth-source"] = "cy-sso";
      return req;
    },
    (e) => Promise.reject(e),
  );

  this.axios.interceptors.response.use(
    (res) => res,
    async (e) => {
      const originalRequest = e.config;

      // Deterministic cross-app logout: a STUDENT logged out / expired in the
      // other app (portal ⇄ sandbox) and raised the shared `cy_logged_out`
      // cookie. On ANY 401 here, trust that signal and end the session
      // immediately — no fragile 401-counting, no refresh attempt that would
      // just spin the page. Guarded so it fires once, and only when we actually
      // have a local session (avoids a redirect loop on the public login page).
      // STUDENT-ONLY, symmetric with setLoggedOutFlag: a parent/staff session is
      // app-local and must never be torn down by a student's cross-app logout
      // signal (e.g. a student logging out elsewhere on a shared browser).
      const localRole = getTokenObj()?.role;
      if (
        e.response?.status === 401 &&
        isLoggedOut() &&
        localStorage.getItem("user") &&
        (!localRole || localRole === "student") &&
        !sessionEnded
      ) {
        sessionEnded = true;
        sessionExpired();
        return Promise.reject(e);
      }

      // Trigger the refresh/expiry path on ANY 401 (not just
      // msg === "Invalid Authorization"). A token invalidated by logout
      // (cy-sso api/logout) can come back 401 with a different message, and
      // previously that slipped through and never redirected to login.
      // NOTE: 403 (and every other non-401 status) is NOT a logout — it's a
      // permission/forbidden error.
      if (
        e.response &&
        e.response.status === 401 &&
        !originalRequest._retry
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            storeExpiredApis((token, err) => {
              if (err) return reject(err);
              // Mark queued retries too, so a still-401 retry is counted by the
              // forgiving threshold below instead of kicking off a 2nd refresh.
              originalRequest._retry = true;
              originalRequest.headers["Authorization"] = "Bearer " + token;
              resolve(this.axios(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        // Snapshot the token whose refresh we're about to attempt — the
        // race-lost recovery below compares sibling stores against it.
        const failedAccessToken = getTokenObj()?.ACCESS_TOKEN;

        try {
          let accessToken, refreshToken;

          if (window.__SANDBOX_IS_PLUGIN__) {
            // MFE mode — use shared coordinator so portal's own interceptor and
            // Sandbox's delegate don't race each other. The coordinator handles
            // updating both localStorage.user and localStorage.sandbox_user.
            const tokens = await doMfeTokenRefresh();
            accessToken = tokens.accessToken;
            refreshToken = tokens.refreshToken;
          } else {
            // Standalone mode — refresh directly
            const tokenObj = getTokenObj();
            if (!tokenObj) {
              onRefreshFailed(e);
              sessionExpired();
              return Promise.reject(e);
            }
            ({ accessToken, refreshToken } = await refreshTokens(tokenObj));

            // Persist through the common saveSession — a raw localStorage write
            // here left the shared cross-app cookies stale, so the other app's
            // cookie-sync (bootstrap / focus listener) could adopt rotated-away
            // tokens and kill the session.
            const prevUser = getTokenObj() || {};
            saveSession({ accessToken, refreshToken, userData: prevUser });
          }

          originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
          originalRequest.headers["refresh-token"] = refreshToken;
          onRefreshed(accessToken);
          return this.axios(originalRequest);
        } catch (refreshError) {
          // Lost refresh race → adopt the winning context's tokens instead of
          // failing. Skipped when the MFE coordinator already ran this exact
          // recovery on this page (raceRecoveryAttempted).
          if (
            isRefreshRaceLost(refreshError) &&
            !refreshError.raceRecoveryAttempted &&
            failedAccessToken
          ) {
            const adopted = await adoptTokensFromSiblingContext(failedAccessToken);
            if (adopted) {
              const prevUser = getTokenObj() || {};
              saveSession({
                accessToken: adopted.accessToken,
                refreshToken: adopted.refreshToken,
                userData: prevUser,
              });
              originalRequest.headers["Authorization"] = `Bearer ${adopted.accessToken}`;
              originalRequest.headers["refresh-token"] = adopted.refreshToken;
              onRefreshed(adopted.accessToken);
              return this.axios(originalRequest);
            }
          }
          logAuthError("token-refresh", refreshError);
          console.error("Token refresh failed:", refreshError);
          onRefreshFailed(refreshError);
          if (window.ReactNativeWebView) {
            const mobileData = {
              type: "route",
            };
            window.ReactNativeWebView.postMessage(JSON.stringify(mobileData));
          } else if (
            isGenuineRefreshFailure(refreshError) &&
            !refreshError.sessionExpiredHandled &&
            !sessionEnded
          ) {
            // Log out only on a GENUINE refresh failure — the server responded
            // (401 invalid tokens, 500, or 200 with valid:false). A pure
            // connectivity error (offline / no `response`) keeps the session, so
            // a user who just lost internet is never logged out. sessionExpiredHandled
            // is set by doMfeTokenRefresh in MFE mode (it already ran the expiry flow).
            sessionEnded = true;
            sessionExpired();
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Post-refresh 401: the refresh succeeded (we have a fresh token) but THIS
      // request is STILL unauthorized. With a new token every API should work, so
      // a 401 here means the session is genuinely dead → log out immediately. No
      // tolerance, no counting.
      if (
        e.response &&
        e.response.status === 401 &&
        originalRequest?._retry &&
        !window.ReactNativeWebView &&
        !e.sessionExpiredHandled &&
        !sessionEnded
      ) {
        sessionEnded = true;
        sessionExpired();
      }

      return Promise.reject(e);
    },
  );
};

createApiClient.prototype.getApiBase = function () {
  return this.apiBase;
};

createApiClient.prototype.get = function (url, params = {}, data = {}, config = {}) {
  return this.axios({
    method: "get",
    url,
    params,
    data,
    ...config,
  });
};

createApiClient.prototype.post = function (url, data = {}, params = {}, config = {}) {
  return this.axios({
    method: "post",
    url,
    data,
    params,
    ...config,
  });
};

createApiClient.prototype.put = function (url, data, params = {}, config = {}) {
  return this.axios({
    method: "put",
    url,
    data,
    params,
    ...config,
  });
};

createApiClient.prototype.delete = function (url, data, params = {}, config = {}) {
  return this.axios({
    method: "delete",
    url,
    data,
    params,
    ...config,
  });
};

export { createApiClient };
